// Imports
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components/macro';
import Checkbox from '../../../../../../../shareables/foundation/front-end/components/forms/fields/checkbox';
import File from '../../../../../../../shareables/foundation/front-end/components/forms/fields/file';
import Input from '../../../../../../../shareables/foundation/front-end/components/forms/fields/input';
import Phone from '../../../../../../../shareables/foundation/front-end/components/forms/fields/phone';
import Radio from '../../../../../../../shareables/foundation/front-end/components/forms/fields/radio';
import Textarea from '../../../../../../../shareables/foundation/front-end/components/forms/fields/textarea';
import MultiCheckboxContainer from '../../../../../../../shareables/foundation/front-end/components/forms/multi-checkbox-container';
import { useAppDispatch } from '../../../../../../../shareables/foundation/front-end/redux/hooks';
import APIForm, { APIFormOptions } from '../../../../../../../shareables/integrations/api/components/form';
import apiActions from '../../../../../../../shareables/integrations/api/redux/actions';
import { GetFormResponse } from '../../../../../../../shareables/types/api/main/website/page/form/get';
import {
	PostMessageFiles,
	PostMessageParameters,
} from '../../../../../../../shareables/types/api/main/website/page/form/message/post';
import removePersistedFormEntry from '../../../../../../../utils/remove-persisted-form-entry';
import { GeneratedThemeProps } from '../../../../../../templates/template-style';
import FormSubmitted from '../form-submitted';


// Styled components
const Container = styled.div<{ theme: GeneratedThemeProps }>`
	line-height: 1.3125rem;
`;


// Define the accepted props
interface Props {
	pageID: number;
	storybookForm?: GetFormResponse;
	storybookSubmitted?: true;
}


// Define responses for state
interface CheckboxResponse {
	id: number;
	answers: (string | undefined)[];
}

interface FileResponse {
	id: number;
	file: File;
}


// Function component
const CustomFormBuilder: React.FC<Props> = ({ pageID, storybookForm, storybookSubmitted }) => {
	// Use state
	const [form, setForm] = useState<GetFormResponse | null>(storybookForm ?? null);
	const [textAndRadioResponses, setTextAndRadioResponses] = useState<PostMessageParameters['responses']>([]);
	const [recipients, setRecipients] = useState<PostMessageParameters['recipients']>([]);
	const [recipientError, setRecipientError] = useState(false);
	const [checkboxResponses, setCheckboxResponses] = useState<CheckboxResponse[]>([]);
	const [checkboxErrors, setCheckboxErrors] = useState<Set<number>>(new Set());
	const [allFileResponses, setAllFileResponses] = useState<FileResponse[]>([]);
	const [imageFileResponses, setImageFileResponses] = useState<FileResponse[]>([]);
	const [pdfFileResponses, setPDFFileResponses] = useState<FileResponse[]>([]);
	const [fileError, setFileError] = useState<{ id: number; message: string } | null>(null);
	const [formSubmitted, setFormSubmitted] = useState(storybookSubmitted ?? false);
	
	
	// Use React Router functionality
	const location = useLocation();
	
	
	// Use Redux functionality
	const dispatch = useAppDispatch();
	
	
	// Load form on mount
	useEffect(() => {
		void dispatch(
			apiActions.call<undefined, undefined, GetFormResponse>({
				action: 'GET',
				uri: `/website/${window.location.hostname}/page/id_${pageID}/form`,
				completion: (json) => {
					setForm(json.response);
					setRecipients(() => (json.response.recipients.length === 1 ? [json.response.recipients[0].id] : []));
				},
			})
		);
	}, [dispatch, pageID]);
	
	
	// Reset form submission state on location change
	//  -> Allows the user to see the form after submission, when clicking link to page they're already on
	useEffect(() => {
		if (!(window as typeof window & { STORYBOOK?: true }).STORYBOOK) {
			setFormSubmitted(false);
		}
	}, [location]);
	
	
	// Use ref
	const formRef = useRef<HTMLFormElement | null>(null);
	
	
	// If loading, then display nothing
	if (form === null) {
		return null;
	}
	
	
	// Function that handles text and radio button type elements
	const handleTextAndRadioChange = (answer: string, elementID: number) => {
		setTextAndRadioResponses((previousTextAndRadioResponses) => {
			// If answer is an empty, remove the response entirely
			if (answer.length === 0) {
				return previousTextAndRadioResponses.filter((existingResponse) => existingResponse.id !== elementID);
			}
			
			
			// Check if there's an existing response
			const existingResponseIndex = previousTextAndRadioResponses.findIndex(
				(existingResponse) => existingResponse.id === elementID
			);
			
			if (existingResponseIndex !== -1) {
				// Update existing response
				const updatedResponses = [...previousTextAndRadioResponses];
				updatedResponses[existingResponseIndex] = { ...updatedResponses[existingResponseIndex], response: answer };
				
				
				// Return
				return updatedResponses;
			}
			
			
			// Add new response
			return [...previousTextAndRadioResponses, { id: elementID, response: answer }];
		});
	};
	
	
	// Function that handles checkbox type elements
	const handleCheckboxChange = (addAnswer: boolean, elementID: number, answerIndex: number, answer: string) => {
		setCheckboxResponses((previousCheckboxResponses) => {
			// Initialize
			const updatedCheckboxResponses = [...previousCheckboxResponses];
			
			
			// Attempt to get index of an existing response
			const existingResponseIndex = updatedCheckboxResponses.findIndex(
				(existingResponse) => existingResponse.id === elementID
			);
			
			
			// Handle adding an answer
			if (addAnswer) {
				if (existingResponseIndex !== -1) {
					// Add new answer to existing response
					updatedCheckboxResponses[existingResponseIndex].answers[answerIndex] = `✔ ${answer}`;
				} else {
					// Create new response
					const newResponse: CheckboxResponse = { id: elementID, answers: [] };
					newResponse.answers[answerIndex] = `✔ ${answer}`;
					
					
					// Add new response
					updatedCheckboxResponses.push(newResponse);
					
					
					// Check if element has an invalid error
					if (formRef.current && checkboxErrors.has(elementID)) {
						// Find first checkbox for this form element
						const firstCheckbox = Array.from(formRef.current.elements).find(
							(element) => element instanceof HTMLInputElement && element.name.startsWith(`${elementID}-0`)
						);
						
						if (firstCheckbox && firstCheckbox instanceof HTMLInputElement) {
							// Reset custom validity error message
							firstCheckbox.setCustomValidity('');
							
							
							// Remove element from error state
							setCheckboxErrors((previousCheckboxErrors) => {
								const updatedCheckboxErrors = new Set(previousCheckboxErrors);
								updatedCheckboxErrors.delete(elementID);
								return updatedCheckboxErrors;
							});
						}
					}
				}
			} else {
				// Remove answer
				updatedCheckboxResponses[existingResponseIndex].answers[answerIndex] = undefined;
				
				
				// Remove the response entirely if there are no valid answers left
				const hasValidAnswers = updatedCheckboxResponses[existingResponseIndex].answers.some(Boolean);
				
				if (!hasValidAnswers) {
					updatedCheckboxResponses.splice(existingResponseIndex, 1);
				}
			}
			
			
			// Return
			return updatedCheckboxResponses;
		});
	};
	
	
	// Helper function to update file responses
	const updateFileResponses = (previousFileResponses: FileResponse[], elementID: number, newFile: File | null) => {
		// Initialize
		const updatedFileResponses = [...previousFileResponses];
		
		
		// Attempt to get index of an existing response
		const existingResponseIndex = updatedFileResponses.findIndex((response) => response.id === elementID);
		
		
		// Handle adding a file
		if (newFile) {
			if (existingResponseIndex !== -1) {
				// Replace existing response
				updatedFileResponses[existingResponseIndex].file = newFile;
			} else {
				// Create new response
				updatedFileResponses.push({ id: elementID, file: newFile });
			}
		} else {
			// Remove existing file
			if (existingResponseIndex !== -1) {
				updatedFileResponses.splice(existingResponseIndex, 1);
			}
		}
		
		
		// Reset error message if required
		if (fileError?.id === elementID) {
			setFileError(null);
		}
		
		
		// Return
		return updatedFileResponses;
	};
	
	
	// Function that handles file type elements
	const handleFileChange = (newFile: File | null, elementID: number, option: 'ALL' | 'IMAGES' | 'PDFS') => {
		// Based on the option, call the appropriate state setter function
		if (option === 'ALL') {
			setAllFileResponses((previousResponses) => updateFileResponses(previousResponses, elementID, newFile));
		}
		
		if (option === 'IMAGES') {
			setImageFileResponses((previousResponses) => updateFileResponses(previousResponses, elementID, newFile));
		}
		
		if (option === 'PDFS') {
			setPDFFileResponses((previousResponses) => updateFileResponses(previousResponses, elementID, newFile));
		}
	};
	
	
	// Function that adds and removes recipients
	const handleRecipientChange = (addRecipient: boolean, recipientID: number) => {
		setRecipients((previousRecipients) => {
			// Initialize
			const updatedRecipients = new Set(previousRecipients);
			
			if (addRecipient) {
				// Add recipient
				updatedRecipients.add(recipientID);
				
				
				// Check if recipient has an invalid error
				if (formRef.current && recipientError) {
					// Find first checkbox for recipients
					const firstCheckbox = Array.from(formRef.current.elements).find(
						(element) => element instanceof HTMLInputElement && element.name.startsWith('recipients-0')
					);
					
					if (firstCheckbox && firstCheckbox instanceof HTMLInputElement) {
						// Reset custom validity error message
						firstCheckbox.setCustomValidity('');
						
						
						// Reset error state
						setRecipientError(false);
					}
				}
			} else {
				// Remove recipient
				updatedRecipients.delete(recipientID);
			}
			
			
			// Return
			return [...updatedRecipients];
		});
	};
	
	
	// Build form options
	const formOptions: APIFormOptions<PostMessageParameters, PostMessageFiles, undefined> = {
		action: 'POST',
		uri: `/website/${window.location.hostname}/page/id_${pageID}/form/message`,
		submitButton: {
			text: 'Submit',
		},
		extraParameters: {
			responses: [
				...textAndRadioResponses,
				...checkboxResponses.map((checkboxResponse) => ({
					id: checkboxResponse.id,
					response: checkboxResponse.answers.filter((answer) => answer).join('\n'),
				})),
				...allFileResponses.map((response, i) => ({ id: response.id, file: i })),
				...imageFileResponses.map((response, i) => ({ id: response.id, file: i })),
				...pdfFileResponses.map((response, i) => ({ id: response.id, file: i })),
			],
			recipients: recipients,
		},
		extraFiles: {
			...(allFileResponses.length > 0 ? { files: allFileResponses.map((response) => response.file) } : undefined),
			...(imageFileResponses.length > 0 ? { images: imageFileResponses.map((response) => response.file) } : undefined),
			...(pdfFileResponses.length > 0 ? { pdfs: pdfFileResponses.map((response) => response.file) } : undefined),
		},
		errorHandlers: [
			{
				match: {
					code: 'FILE_ERROR',
				},
				handler: (json) => {
					if (json.file) {
						// Get file option and index
						const match = json.file.match(/(\w+)\[(\d+)\]/);
						
						if (match) {
							// Initialize
							const fileOption = match[1];
							const fileIndex = parseInt(match[2]);
							let elementID = 0;
							
							
							// Match file option and index to element ID
							if (fileOption === 'files') {
								elementID = allFileResponses[fileIndex].id;
							}
							
							if (fileOption === 'images') {
								elementID = imageFileResponses[fileIndex].id;
							}
							
							if (fileOption === 'pdfs') {
								elementID = pdfFileResponses[fileIndex].id;
							}
							
							setFileError({ id: elementID, message: json.message });
						}
					}
				},
			},
		],
	};
	
	
	// Handle form completion
	const handleFormCompletion = () => {
		// Reset form
		removePersistedFormEntry(`POST /website/${window.location.hostname}/page/id_${pageID}/form/message`);
		
		
		// Display success
		setFormSubmitted(true);
	};
	
	
	// Return JSX
	return (
		<Container>
			{form.elements.length === 0 ? (
				<em>No elements have been added to this form by an admin yet…</em>
			) : form.recipients.length === 0 ? (
				<em>No recipients have been added for this form by an admin yet…</em>
			) : formSubmitted ? (
				<FormSubmitted
					header='Form submitted!'
					body='You’ve successfully submitted this form, and your message will be handled shortly.'
				/>
			) : (
				<APIForm options={formOptions} onCompletion={handleFormCompletion} ref={formRef}>
					{form.elements.map((element) => {
						// Initialize base props
						const baseInputProps = {
							key: element.id,
							label: element.prompt,
							required: element.required,
							name: element.id.toString(),
							exclude: true,
							note: element.required ? '' : 'Optional',
						};
						
						
						// Render input based on element type
						if (element.type === 'TEXT') {
							// Initialize
							const textInputProps = {
								...baseInputProps,
								onChange: (answer: string) => handleTextAndRadioChange(answer, element.id),
							};
							
							
							// Handle different options
							if (element.option === 'SMALL') {
								return <Input {...textInputProps} type='text' />;
							}
							
							if (element.option === 'MEDIUM') {
								return <Textarea {...textInputProps} rows={4} />;
							}
							
							if (element.option === 'LARGE') {
								return <Textarea {...textInputProps} rows={14} />;
							}
							
							if (element.option === 'EMAIL') {
								return <Input {...textInputProps} type='email' />;
							}
							
							if (element.option === 'PHONE') {
								return <Phone {...textInputProps} />;
							}
							
							if (element.option === 'DATE') {
								return <Input {...textInputProps} type='date' />;
							}
							
							if (element.option === 'DATETIME') {
								return <Input {...textInputProps} type='datetime-local' outputRaw />;
							}
						}
						
						if (element.type === 'CHECKBOXES') {
							return (
								<MultiCheckboxContainer
									key={element.id}
									label={element.prompt}
									note={element.required ? '' : 'Optional'}
								>
									{element.answers.map((answer, index) => (
										<Checkbox
											key={`${element.id}-${index}-${answer}`}
											label={answer}
											name={`${element.id}-${index}-${answer}`}
											exclude
											onChange={(addAnswer) => handleCheckboxChange(addAnswer, element.id, index, answer)}
											/* If this form element is required, mark only the first answer as required and set custom error message when no checkbox is checked. The onChange handler will reset the validity message. */
											required={
												element.required &&
												!checkboxResponses.some((response) => response.id === element.id) &&
												index === 0
											}
											onInvalid={(e) => {
												(e.target as HTMLInputElement).setCustomValidity(
													element.answers.length === 1
														? 'Please check this option to proceed'
														: 'Please select at least one of these options'
												);
												
												setCheckboxErrors((previousCheckboxErrors) => {
													const updatedCheckboxErrors = new Set(previousCheckboxErrors);
													updatedCheckboxErrors.add(element.id);
													return updatedCheckboxErrors;
												});
											}}
										/>
									))}
								</MultiCheckboxContainer>
							);
						}
						
						if (element.type === 'RADIO_BUTTONS') {
							return (
								<Radio
									{...baseInputProps}
									choices={element.answers.map((answer) => ({ label: answer, value: answer }))}
									onChange={(answer) => handleTextAndRadioChange(answer, element.id)}
								/>
							);
						}
						
						if (element.type === 'FILE') {
							return (
								<File
									{...baseInputProps}
									{...(element.option === 'IMAGES'
										? { accept: 'image/*' }
										: element.option === 'PDFS'
											? { accept: 'application/pdf' }
											: {})}
									onChange={(newFile) => handleFileChange(newFile, element.id, element.option)}
									errorMessage={fileError?.id === element.id ? fileError.message : ''}
								/>
							);
						}
					})}
					
					<MultiCheckboxContainer label='Choose recipient(s)'>
						{form.recipients.map((recipient, index) => (
							<Checkbox
								key={`recipients-${index}-${recipient.id}`}
								label={`${'name' in recipient && recipient.name ? `${recipient.name} - ` : ''}${recipient.title}`}
								name={`recipients-${index}-${recipient.id}`}
								exclude
								onChange={(addRecipient) => handleRecipientChange(addRecipient, recipient.id)}
								initiallyChecked={form.recipients.length === 1}
								disabled={form.recipients.length === 1}
								/* Mark only the first recipient as required and set custom error message when no checkbox is checked. The onChange handler will reset the validity message. */
								required={index === 0 && recipients.length === 0}
								onInvalid={(e) => {
									(e.target as HTMLInputElement).setCustomValidity('Please select at least one of these options');
									setRecipientError(true);
								}}
							/>
						))}
					</MultiCheckboxContainer>
				</APIForm>
			)}
		</Container>
	);
};

export default CustomFormBuilder;
